import {
  Button,
  Tooltip,
  FormGroup,
  LoadingSpinner,
  PlainButton,
  Menu,
} from "@jugl-web/ui-kit";
import { cx, useTranslations } from "@jugl-web/utils";
import React, { ReactNode } from "react";
import { InfoIndicator } from "@web-src/modules/client-support/pages/ClientSupportPage/components/ManageAccountsDrawer/components/InfoIndicator";
import { NotRemovableReasonHint } from "@web-src/modules/entities/components/NotRemovableReasonHint";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";
import { ReactComponent as MoreIcon } from "./assets/more.svg";
import { ReactComponent as DisconnectIcon } from "./assets/disconnect.svg";
import { ReactComponent as InfoIcon } from "./assets/info.svg";

export type CXIntegrationTileProps = {
  title: string;
  description: string;
  icon: React.ReactNode;
  isConnected: boolean;
  needsReconnect: boolean;
  isExpired: boolean;
  isConnecting: boolean;
  isReconnecting: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
  onReconnect: () => void;
  pendingLabel?: string;
  pendingTooltip?: string;
  onHowItWorks?: () => void;
  isActivationDisabled?: boolean;
  activationDisabledTooltip?: string;
  isDisconnectDisabled?: boolean;
  disconnectDisabledReason?: string;
};

export const CXIntegrationTile: React.FC<CXIntegrationTileProps> = ({
  title,
  description,
  needsReconnect,
  isConnected,
  isExpired,
  isConnecting,
  icon,
  onDisconnect,
  isReconnecting,
  onConnect,
  onReconnect,
  pendingLabel,
  pendingTooltip,
  onHowItWorks,
  isActivationDisabled = false,
  activationDisabledTooltip,
  isDisconnectDisabled = false,
  disconnectDisabledReason,
}) => {
  const { t } = useTranslations();
  const showReconnectAction = isConnected && needsReconnect;
  const showConnectAction = !isConnected || isExpired;

  const showWarningBorder = isExpired || (isConnected && needsReconnect);

  let overlapLabel: string | undefined;
  if (isExpired) {
    overlapLabel = t({
      id: "cx-actions-page.connection-expired",
      defaultMessage: "Expired",
    });
  } else if (isConnected && needsReconnect) {
    overlapLabel = t({
      id: "common.disconnected",
      defaultMessage: "Disconnected",
    });
  }

  return (
    <FormGroup
      classNames={{
        wrapperClassName: "min-w-[440px] max-w-[480px]",
        labelClassName: "text-tertiary-800",
      }}
      isInvalid={showWarningBorder}
      labelPosition="border-overlap"
      label={overlapLabel}
    >
      <div
        className={cx(
          "flex h-full w-full items-start gap-4 rounded-xl border border-solid border-grey-300 p-5",
          showWarningBorder && "border-tertiary-300"
        )}
      >
        <div
          className="flex h-full w-[100px] shrink-0 items-center justify-center rounded-xl"
          style={{
            backgroundImage:
              "radial-gradient(circle, #E1E4E6 1px, #FCFCFC 1.5px)",
            backgroundSize: "8px 8px",
          }}
        >
          {icon}
        </div>
        <div className="flex flex-col overflow-hidden">
          <div className="flex items-center justify-between gap-2.5">
            <span className="truncate font-secondary text-xl leading-[30px] font-medium text-black">
              {title}
            </span>
            <div className="flex items-center gap-2.5">
              <Menu
                autoClose
                placement="bottom"
                renderTrigger={({ Trigger, triggerRef, isOpen }) => (
                  <Trigger
                    ref={triggerRef}
                    as={PlainButton}
                    className={cx(
                      "flex h-8 w-8 items-center justify-center rounded-[10px] border border-solid border-[#F2F2F2] ring-[#F2F2F2] transition ring-inset hover:ring-1",
                      isOpen && "ring-1"
                    )}
                  >
                    <MoreIcon />
                  </Trigger>
                )}
                sections={[
                  [
                    {
                      id: "how-it-works",
                      label: t({
                        id: "cx-actions-page.how-it-works",
                        defaultMessage: "How it works",
                      }),
                      icon: <InfoIcon />,
                      onSelect: onHowItWorks,
                      isHidden: !onHowItWorks,
                    },
                    {
                      id: "disconnect",
                      label: t({
                        id: "common.disconnect",
                        defaultMessage: "Disconnect",
                      }),
                      icon: (
                        <DisconnectIcon
                          className={isDisconnectDisabled ? "opacity-25" : ""}
                        />
                      ),
                      endSlot: disconnectDisabledReason ? (
                        <NotRemovableReasonHint
                          reason={disconnectDisabledReason}
                        />
                      ) : undefined,
                      labelClassName: isDisconnectDisabled
                        ? "opacity-25"
                        : undefined,
                      onSelect: () => {
                        if (isDisconnectDisabled) {
                          return;
                        }
                        onDisconnect();
                      },
                      isHidden: !isConnected || isExpired,
                    },
                  ],
                ]}
              />
            </div>
          </div>
          <Tooltip
            renderTrigger={({ props, ref }) => (
              <span
                className="mt-2 line-clamp-2 cursor-default font-secondary text-sm leading-[21px] text-[#737B7E]"
                ref={ref}
                {...props}
              >
                {description}
              </span>
            )}
          >
            {description}
          </Tooltip>
          <div className="mt-5">
            {showReconnectAction ? (
              <div className="flex items-center gap-4">
                <Button
                  variant="secondary"
                  className="h-8"
                  iconEnd={
                    isReconnecting ? (
                      <LoadingSpinner size="xs" color="grey" />
                    ) : (
                      <ArrowIcon />
                    )
                  }
                  isDisabled={isReconnecting || isActivationDisabled}
                  onClick={onReconnect}
                >
                  {isReconnecting
                    ? t({
                        id: "cx-actions-page.reconnecting",
                        defaultMessage: "Reconnecting...",
                      })
                    : (pendingLabel ??
                      t({
                        id: "cx-actions-page.reconnect",
                        defaultMessage: "Reconnect",
                      }))}
                </Button>
                <InfoIndicator
                  message={
                    (isActivationDisabled && activationDisabledTooltip) ||
                    pendingTooltip ||
                    t(
                      {
                        id: "cx-actions-page.account-disconnected",
                        defaultMessage:
                          "Account was disconnected. All related actions are disabled. Reconnect to your <underlineText>{integration} account</underlineText> to enable the Actions",
                      },
                      {
                        integration: title,
                        underlineText: (text: ReactNode) => (
                          <span className="underline">{text}</span>
                        ),
                      }
                    )
                  }
                />
              </div>
            ) : showConnectAction ? (
              <div className="flex items-center gap-4">
                <Button
                  variant="secondary"
                  className="h-8"
                  onClick={onConnect}
                  isDisabled={isConnecting || isActivationDisabled}
                  iconEnd={
                    isConnecting ? (
                      <LoadingSpinner size="xs" color="grey" />
                    ) : (
                      <ArrowIcon />
                    )
                  }
                >
                  {isConnecting
                    ? t({
                        id: "common.connecting",
                        defaultMessage: "Connecting...",
                      })
                    : t({
                        id: "common.connect",
                        defaultMessage: "Connect",
                      })}
                </Button>
                {isActivationDisabled && activationDisabledTooltip ? (
                  <InfoIndicator message={activationDisabledTooltip} />
                ) : null}
              </div>
            ) : (
              <div className="flex h-8 w-max items-center gap-2.5 rounded-[10px] border border-solid border-[#F2F2F2] px-4">
                <div className="h-2.5 w-2.5 rounded-full bg-[#22C59E]" />
                <span className="font-secondary text-sm font-medium text-dark-800">
                  {t({
                    id: "common.connected",
                    defaultMessage: "Connected",
                  })}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </FormGroup>
  );
};
